import { useEffect, useState, type FormEvent } from "react";
import api from "@/api/axios";
import { Link2, RefreshCw } from "lucide-react";

const statusLabel = (status: string) => status === "loading" ? "检查中..." : status === "connected" ? "已连接" : "未连接";

/** 后端仅支持手机号验证码 REST 登录流程。 */
export default function SessionPanel({ status, refresh }: { status: string; refresh: () => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [sessionStatus, setSessionStatus] = useState(statusLabel(status));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [floodWait, setFloodWait] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  useEffect(() => {
    if (floodWait <= 0) return;
    const timer = window.setInterval(() => setFloodWait((value) => Math.max(value - 1, 0)), 1000);
    return () => window.clearInterval(timer);
  }, [floodWait]);

  useEffect(() => setSessionStatus(statusLabel(status)), [status]);

  const showError = (error: unknown, fallback: string) => {
    const detail: any = (error as any)?.response?.data?.detail;
    const errorCode = typeof detail === "object" ? detail.code : undefined;
    const message = typeof detail === "object" ? (detail.message || fallback) : (detail || fallback);
    const retryAfter = typeof detail === "object" ? (detail.retryAfter ?? detail.retry_after) : undefined;
    if (errorCode === "FLOOD_WAIT" && typeof retryAfter === "number") {
      setFloodWait(retryAfter);
      alert(`${message}（请在 ${retryAfter} 秒后重试）`);
    } else if (errorCode === "INVALID_PHONE") {
      alert("手机号格式无效，请检查区号与号码");
    } else if (errorCode === "PASSWORD_REQUIRED" || errorCode === "INVALID_PASSWORD") {
      setShowPasswordInput(true);
      alert(errorCode === "PASSWORD_REQUIRED" ? "需要二步验证密码，请输入后再次提交" : "二步验证密码错误，请重试");
    } else if (errorCode === "INVALID_CODE") {
      alert("验证码错误，请重新输入");
    } else if (errorCode === "CODE_EXPIRED") {
      alert("验证码已过期，请重新获取");
    } else if (errorCode === "PHONE_BANNED") {
      alert("该手机号被限制或封禁，无法连接");
    } else if (["NETWORK_ERROR", "MISCONFIGURED", "UNKNOWN", "INTERNAL_ERROR"].includes(errorCode)) {
      alert("服务器内部错误，请稍后再试");
    } else {
      alert(message);
    }
  };

  const requestCode = async () => {
    if (!phone) return alert("请先填写手机号（包含国家区号，如 +86）");
    if (isRequestingCode || floodWait > 0) return;
    setIsRequestingCode(true);
    try {
      const response = await api.post("/api/request_code", { phone }, { timeout: 20000 });
      alert(response.data.message || "验证码已发送");
    } catch (error) {
      showError(error, "获取验证码失败");
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsConnecting(true);
    try {
      await api.post("/api/connect_session", { phone, code, password }, { timeout: 20000 });
      alert("连接成功！");
      setSessionStatus("已连接");
      refresh();
    } catch (error) {
      showError(error, "连接失败，请检查信息");
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setSessionStatus("检查中...");
    try {
      const response = await api.get("/api/check_session");
      setSessionStatus(response.data.success && response.data.session_available ? "已连接" : "未连接");
    } catch (error) {
      console.error("检查连接状态失败:", error);
      setSessionStatus("未连接");
    } finally {
      refresh();
      setIsRefreshing(false);
    }
  };

  return (
    <div className="user-session-single">
      <section className="user-card">
        <div className="user-card-heading"><h2>连接 Telegram</h2><div className="user-card-heading-icon"><Link2 size={20} /></div></div>
        <div className="user-session-status"><span className={`user-status-badge ${sessionStatus === "已连接" ? "is-on" : ""}`}><i />{sessionStatus}</span><button className="user-button user-button-secondary" onClick={handleRefresh} disabled={isRefreshing}><RefreshCw size={15} className={isRefreshing ? "animate-spin" : ""} />刷新状态</button></div>
        <form onSubmit={handleSubmit} className="user-session-form">
          <label>手机号<span>包含国家区号，如 +86138...</span><input type="tel" autoComplete="tel" placeholder="请输入手机号" value={phone} onChange={(event) => setPhone(event.target.value)} /></label>
          <label>验证码<div className="user-inline-field"><input type="text" inputMode="numeric" placeholder="请输入收到的验证码" value={code} onChange={(event) => setCode(event.target.value)} /><button type="button" className="user-button user-button-secondary" onClick={requestCode} disabled={isRequestingCode || floodWait > 0}>{isRequestingCode ? "发送中..." : floodWait > 0 ? `请稍后 (${floodWait}s)` : "获取验证码"}</button></div></label>
          {showPasswordInput && <div className="user-inline-notice">此账号启用了二步验证，请填写密码后再次连接。</div>}
          <label>二步验证密码<span>未开启时可留空</span><input type="password" autoComplete="current-password" placeholder="如已开启二步验证，请输入密码" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <button type="submit" disabled={isConnecting} className="user-button user-button-primary user-session-submit">{isConnecting ? "连接中..." : "连接账号"}</button>
        </form>
      </section>
    </div>
  );
}
